'use client';

import { useEffect, useRef, useState } from 'react';
import { Loader2, Package, Printer, Search, X } from 'lucide-react';
import { type ShipRecord, openPrintWindow } from '../../lib/printShipment';


function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function RecentShipments() {
  const [recentRecords, setRecentRecords] = useState<ShipRecord[]>([]);
  const [searchResults, setSearchResults] = useState<ShipRecord[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Initial load — top 10 recent
  useEffect(() => {
    fetch('/api/ship-history')
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((data) => setRecentRecords(Array.isArray(data) ? data : []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!search.trim()) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      fetch(`/api/ship-history?q=${encodeURIComponent(search.trim())}`)
        .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
        .then((data) => setSearchResults(Array.isArray(data) ? data : []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 350);
  }, [search]);

  const isSearchActive = search.trim().length > 0;
  const displayRecords = isSearchActive ? searchResults : recentRecords;
  const isSpinning = loading || (isSearchActive && searching);

  return (
    <div className="flex flex-col h-full">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Package className="w-3.5 h-3.5 text-primary" />
          </div>
          <h2 className="text-base font-semibold text-gray-900">Recent Shipments</h2>
        </div>
      </div>

      {/* ── Search ──────────────────────────────────────────────────── */}
      <div className="relative mb-3">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Name, email, or invoice #…"
          className="w-full h-8 pl-8 pr-7 rounded-md border border-border bg-white text-[12px] text-gray-800
                     placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-interactive/40 focus:border-primary/50 transition"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-gray-600"
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* ── States ──────────────────────────────────────────────────── */}
      {isSpinning && (
        <div className="flex items-center justify-center py-10 text-muted">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          <span className="text-sm">{isSearchActive ? 'Searching…' : 'Loading…'}</span>
        </div>
      )}

      {!isSpinning && !loading && error && !isSearchActive && (
        <p className="text-sm text-muted text-center py-8">Unable to load recent shipments.</p>
      )}

      {!isSpinning && displayRecords.length === 0 && (
        <p className="text-sm text-muted text-center py-8">
          {isSearchActive ? 'No shipments match your search.' : 'No shipments yet.'}
        </p>
      )}

      {/* ── List ────────────────────────────────────────────────────── */}
      {!isSpinning && displayRecords.length > 0 && (
        <>
          {isSearchActive && (
            <p className="text-[10px] text-muted mb-2">
              {displayRecords.length} result{displayRecords.length !== 1 ? 's' : ''}
            </p>
          )}
          <ul className="space-y-2">
            {displayRecords.map((r, i) => (
              <li
                key={r.InvoiceNo ?? i}
                className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2.5 hover:bg-gray-100/60 transition-colors group"
              >
                {/* Top row: name + date + print */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-1.5">
                      <p className="text-[13px] font-semibold text-gray-800 truncate leading-snug">
                        {r.Name || '—'}
                      </p>
                      {r.InvoiceNo != null && (
                        <span className="text-[10px] text-muted flex-shrink-0">#{r.InvoiceNo}</span>
                      )}
                    </div>
                    {r.Company && (
                      <p className="text-[11px] text-muted truncate">{r.Company}</p>
                    )}
                    {(r.City || r.State) && (
                      <p className="text-[11px] text-muted">
                        {[r.City, r.State].filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] text-muted whitespace-nowrap mt-0.5">
                      {formatDate(r.SDate)}
                    </span>
                    <button
                      onClick={() => openPrintWindow(r)}
                      title="Print"
                      className="p-1 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-800 transition-colors"
                    >
                      <Printer className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Meta row: carrier, dept, sent by */}
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  {r.Carrier && (
                    <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded font-medium">
                      {r.Carrier}{r.Priority ? ` · ${r.Priority}` : ''}
                    </span>
                  )}
                  {r.Department && (
                    <span className="text-[10px] text-muted">{r.Department}</span>
                  )}
                  {r.SentBy && (
                    <span className="text-[10px] text-muted">by {r.SentBy}</span>
                  )}
                </div>

                {/* Tracking numbers */}
                {(r.ShippingDetails?.length ?? 0) > 0 && (
                  <div className="mt-1.5 flex flex-col gap-0.5">
                    {r.ShippingDetails!.map((d, j) =>
                      d.TrackingNo ? (
                        <span
                          key={j}
                          className="font-mono text-[10px] text-gray-500 tracking-tight leading-snug"
                        >
                          {d.TrackingNo}
                        </span>
                      ) : null,
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
